import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from './store';
import {
    Device,
    OnboardDevicesPagination,
    OnboardDevicesQueryParams,
    OnboardDevicesResponse,
} from '../interfaces';
import { DEFAULT_ONBOARD_DEVICES_PAGE_SIZE, getOnboardDevices } from '../api/getOnboardDevices';

export interface DevicesState {
    onboardDevices: Device[] | null;
    onboardDevicesPagination: OnboardDevicesPagination;
    onboardDevicesLoading: boolean;
    onboardDevicesError: string | null;
    onboardDevicesCurrentRequestId: string | null;
    selectedOnboardDeviceTableRows: string[];
}

const initialState: DevicesState = {
    selectedOnboardDeviceTableRows: [],
    onboardDevices: null,
    onboardDevicesPagination: {
        page: 1,
        pageSize: DEFAULT_ONBOARD_DEVICES_PAGE_SIZE,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
    },
    onboardDevicesLoading: false,
    onboardDevicesError: null,
    onboardDevicesCurrentRequestId: null,
};

export const loadOnboardDevices = createAsyncThunk<
    OnboardDevicesResponse,
    OnboardDevicesQueryParams | undefined
>('devices/loadOnboardDevices', async (params, thunkApi) => {
    return await getOnboardDevices(
        params || { page: 1, pageSize: DEFAULT_ONBOARD_DEVICES_PAGE_SIZE },
        thunkApi.signal,
    );
});

export const DevicesSlice = createSlice({
    name: 'devices',
    initialState,
    reducers: {
        setSelectedOnboardDeviceTableRows: (state, action: PayloadAction<string[]>) => {
            state.selectedOnboardDeviceTableRows = action.payload;
        },
        patchOnboardDeviceById: (
            state,
            action: PayloadAction<{ deviceId: string; patch: Partial<Device> }>,
        ) => {
            const device = state.onboardDevices?.find((item) => item.deviceId === action.payload.deviceId);

            if (device) {
                Object.assign(device, action.payload.patch);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadOnboardDevices.pending, (state, action) => {
                state.onboardDevicesLoading = true;
                state.onboardDevicesError = null;
                state.onboardDevicesCurrentRequestId = action.meta.requestId;
            })
            .addCase(loadOnboardDevices.fulfilled, (state, action) => {
                if (state.onboardDevicesCurrentRequestId !== action.meta.requestId) {
                    return;
                }

                state.onboardDevices = action.payload.items;
                state.onboardDevicesPagination = action.payload.pagination;
                state.onboardDevicesLoading = false;
                state.onboardDevicesCurrentRequestId = null;
            })
            .addCase(loadOnboardDevices.rejected, (state, action) => {
                if (state.onboardDevicesCurrentRequestId !== action.meta.requestId) {
                    return;
                }

                state.onboardDevicesLoading = false;
                state.onboardDevicesCurrentRequestId = null;
                if (!action.meta.aborted) {
                    state.onboardDevicesError = action.error.message || 'Failed to load onboard devices.';
                    console.error(action.error);
                }
            });
    },
});

export const { setSelectedOnboardDeviceTableRows, patchOnboardDeviceById } = DevicesSlice.actions;

export const selectOnboardDevices = (state: RootState) => state.devices.onboardDevices;
export const selectOnboardDevicesPagination = (state: RootState) => state.devices.onboardDevicesPagination;
export const selectOnboardDevicesLoading = (state: RootState) => state.devices.onboardDevicesLoading;
export const selectOnboardDevicesError = (state: RootState) => state.devices.onboardDevicesError;
export const selectSelectedOnboardDeviceTableRows = (state: RootState) =>
    state.devices.selectedOnboardDeviceTableRows;

export default DevicesSlice.reducer;
